import React from 'react';
import { useTranslation } from 'react-i18next';
import { Species } from '../../../../types/gql-op-types';
import { gqlTypes } from '../../../../types';
import { BaseManageSubscriptionState } from '../util/types';
import { getBillingCadenceDescription, getShortSubscriptionName } from '../../util/helpers';
import { centsToDollarsNumber } from '../../../../lib/util';
import { DateTime } from 'luxon';
import DogPlaceholder from '../../../../assets/images/dog_placeholder.png';
import CatPlaceholder from '../../../../assets/images/cat_placeholder.png';
import EditIcon from '../../../../assets/images/icons/edit.svg';
import { Avatar, Button as MantineButton, Card, Group, Text, Stack, ActionIcon, Modal, Drawer } from '@mantine/core';
import { StatusSection } from './StatusSection';
import { Image } from '../../../../components/Image';
import { webSubscriptionManage as events } from '../../../../lib/analytics/events';
import { Link } from 'react-router-dom';
import { Location } from 'history';
import AppPaths from '../../../../AppPaths';
import { ApolloQueryResult, useMutation } from '@apollo/client';
import {
  allSubscriptionsForCurrentUserQuery,
  convertFreeTrialMutation,
  renewSubscriptionMutation,
  resumeMembershipMutation,
} from '../../../../graphql-operations';
import { notifications } from '@mantine/notifications';
import { logInternalError } from '../../../../lib/errors';
import { useDisclosure, useMediaQuery } from '@mantine/hooks';
import Button from '../../../../components/Button';
import { AddSubscriptionModal } from './AddSubscriptionModal';
import { useLocale } from '../../../../contexts/LocaleContext';
import { ConvertTrialConfirmContent } from '../ConvertTrialConfirmContent';

interface SubscriptionCardProps {
  petName: string;
  petSpecies?: Species;
  avatarUrl?: string;
  petId: string;
  hasSubscription: boolean;
  subscriptionId?: string | null;
  moduleId?: string;
  modelName?: string;
  renewalDate?: DateTime;
  expirationDate?: DateTime;
  priceInCents?: number;
  renewalPriceInCents?: number;
  renewalMonths?: number;
  userEmail?: string;
  billingEmail?: string | null;
  isPaused: boolean;
  pausedAt?: DateTime;
  isCanceled: boolean;
  hasFreeTrialPlan: boolean;
  isInTrialPeriod: boolean;
  sku?: string;
  pendingCxResponse?: boolean;
  location: Location<BaseManageSubscriptionState>;
  pendingChange?: { priceInCents: number; term: string };
  lastPaymentFailed: boolean;
  /** Function to refresh subscriptions, required for refreshing after adding new subscription */
  refetchAllSubscriptions: () => Promise<ApolloQueryResult<gqlTypes.ECOMMERCE_allSubscriptionsForCurrentUser>>;
}

export function SubscriptionCard(props: SubscriptionCardProps) {
  const { t } = useTranslation();
  const {
    petName,
    petSpecies,
    petId,
    avatarUrl,
    hasSubscription,
    subscriptionId,
    moduleId,
    modelName,
    renewalDate,
    priceInCents,
    renewalMonths,
    userEmail,
    billingEmail,
    isPaused,
    pausedAt,
    isCanceled,
    hasFreeTrialPlan,
    isInTrialPeriod,
    expirationDate,
    renewalPriceInCents,
    sku,
    pendingCxResponse,
    location,
    pendingChange,
    lastPaymentFailed,
    refetchAllSubscriptions,
  } = props;
  const { currencyFormatter } = useLocale();

  const DATE_FORMAT_TOKEN = 'MMMM d, y';

  const description =
    priceInCents !== undefined && renewalMonths !== undefined
      ? getBillingCadenceDescription(priceInCents, renewalMonths, currencyFormatter)
      : '';

  const canManageSubscription = !!userEmail && !!billingEmail && userEmail === billingEmail;

  const hasDevice = !!moduleId;

  const isDiscounted =
    renewalPriceInCents !== undefined && priceInCents !== undefined && renewalPriceInCents < priceInCents;

  const renewalPriceString =
    renewalPriceInCents !== undefined ? currencyFormatter.format(centsToDollarsNumber(renewalPriceInCents)) : '$X';
  const renewalDateString = renewalDate?.toFormat(DATE_FORMAT_TOKEN) ?? '';

  const isExpired = expirationDate !== undefined && expirationDate < DateTime.now();
  const expirationDateString = expirationDate?.toFormat(DATE_FORMAT_TOKEN) ?? '';

  const isActiveFreeTrial = hasFreeTrialPlan && isInTrialPeriod && !isCanceled && !isExpired;
  const isCanceledFreeTrial = hasFreeTrialPlan && isInTrialPeriod && isCanceled && !isExpired;

  const isCurrentlyPaused = isPaused && pausedAt !== undefined && pausedAt < DateTime.now();
  const willPause = isPaused && !isCurrentlyPaused;

  const renewalPlanName = renewalMonths !== undefined ? getShortSubscriptionName(t, renewalMonths) : '';
  const renewalPriceFormatted =
    priceInCents !== undefined ? currencyFormatter.format(centsToDollarsNumber(priceInCents)) : '';

  let subscriptionDateString = '';
  if (isExpired) {
    subscriptionDateString = t('expired_on_date', { date: expirationDateString });
  } else if (isCanceled) {
    subscriptionDateString = t('expires_on_date', { date: expirationDateString });
  } else if (willPause || lastPaymentFailed) {
    subscriptionDateString = '';
  } else if (isActiveFreeTrial) {
    subscriptionDateString = t('renews_for_plan_price_on_date', {
      plan: renewalPlanName,
      price: renewalPriceFormatted,
      date: renewalDateString,
    });
  } else {
    subscriptionDateString = t('renews_on_date', { date: renewalDateString });
  }

  const avatarPlaceholder = petSpecies === Species.CAT ? CatPlaceholder : DogPlaceholder;
  const showEditIcon = !isCanceled && !isExpired && !isPaused && hasSubscription;

  const [openedResume, { open: openResume, close: closeResume }] = useDisclosure(false);
  const [openedReactivate, { open: openReactivate, close: closeReactivate }] = useDisclosure(false);
  const [openedAddSubscription, { open: openAddSubscription, close: closeAddSubscription }] = useDisclosure(false);
  const [openedConvertTrial, { open: openConvertTrial, close: closeConvertTrial }] = useDisclosure(false);

  const largeScreen = useMediaQuery('(min-width: 48em)');

  const [resumeMutation, resumeMutationState] = useMutation<
    gqlTypes.ECOMMERCE_resumeMembership,
    gqlTypes.ECOMMERCE_resumeMembershipVariables
  >(resumeMembershipMutation, {
    refetchQueries: [{ query: allSubscriptionsForCurrentUserQuery }],
    onCompleted: (response) => {
      const success = response.resumeMembership?.success ?? false;
      if (!success) {
        notifications.show({
          title: t('failed_to_resume_membership'),
          message: t('please_try_again_later_or_contact_support'),
          color: 'red',
          autoClose: false,
        });
        logInternalError(`Failed to resume membership - ${response.resumeMembership?.reasonForFailure}`);
      } else {
        notifications.show({
          message: t('your_membership_has_resumed'),
          color: 'green',
        });
        events.overview.resumeSuccess({
          petId: petId,
          currentSku: sku,
        });
        closeResume();
      }
    },
    onError: (err) => {
      notifications.show({
        title: t('failed_to_resume_membership'),
        message: t('please_try_again_later_or_contact_support'),
        color: 'red',
        autoClose: false,
      });
      logInternalError(err);
    },
  });

  const [renewMutation, renewMutationState] = useMutation<
    gqlTypes.ECOMMERCE_renewSubscription,
    gqlTypes.ECOMMERCE_renewSubscriptionVariables
  >(renewSubscriptionMutation, {
    refetchQueries: [{ query: allSubscriptionsForCurrentUserQuery }],
    onCompleted: (response) => {
      const success = response.renewSubscription?.success ?? false;
      if (!success) {
        notifications.show({
          title: t('failed_to_reactivate_membership'),
          message: t('please_try_again_later_or_contact_support'),
          color: 'red',
          autoClose: false,
        });
        logInternalError(`Failed to reactivate membership`);
      } else {
        notifications.show({
          message: t('your_membership_has_been_reactivated'),
          color: 'green',
        });
        events.overview.reactivateSuccess({
          petId: petId,
          currentSku: sku,
        });
        closeReactivate();
      }
    },
    onError: (err) => {
      notifications.show({
        title: t('failed_to_reactivate_membership'),
        message: t('please_try_again_later_or_contact_support'),
        color: 'red',
        autoClose: false,
      });
      logInternalError(err);
    },
  });

  const [convertTrialMutation, convertTrialMutationState] = useMutation<
    gqlTypes.ECOMMERCE_convertFreeTrial,
    gqlTypes.ECOMMERCE_convertFreeTrialVariables
  >(convertFreeTrialMutation, {
    refetchQueries: [{ query: allSubscriptionsForCurrentUserQuery }],
    onCompleted: (response) => {
      const success = response.convertFreeTrial?.success ?? false;
      if (!success) {
        notifications.show({
          title: t('something_went_wrong'),
          message: t('please_try_again_later_or_contact_support'),
          color: 'red',
          autoClose: false,
        });
        logInternalError(`Failed to convert free trial - ${response.convertFreeTrial?.reasonForFailure}`);
      } else {
        notifications.show({
          message: t('your_membership_has_been_updated'),
          color: 'green',
        });
        closeConvertTrial();
      }
    },
    onError: (err) => {
      notifications.show({
        title: t('something_went_wrong'),
        message: t('please_try_again_later_or_contact_support'),
        color: 'red',
        autoClose: false,
      });
      logInternalError(err);
    },
  });

  const handleResumeClicked = () => {
    if (hasSubscription && subscriptionId && moduleId) {
      openResume();
      events.overview.clickedResume({ petId: petId, currentSku: sku });
    }
  };

  const handleReactivateClicked = () => {
    if (hasSubscription && subscriptionId && moduleId) {
      openReactivate();
      events.overview.clickedReactivate({ petId: petId, currentSku: sku });
    }
  };

  const handleAddSubscription = () => {
    if (moduleId) {
      openAddSubscription();
      events.overview.addSubscriptionOpened({ petId: petId });
    }
  };

  const onCloseAddSubscription = () => {
    events.overview.addSubscriptionClosed({ petId: petId });
    closeAddSubscription();
  };

  const onConfirmResume = () => {
    if (!subscriptionId) {
      notifications.show({
        title: t('failed_to_resume_membership'),
        message: t('please_try_again_later_or_contact_support'),
        color: 'red',
        autoClose: false,
      });
    } else {
      events.overview.confirmedResume({
        petId: petId,
        currentSku: sku,
      });
      resumeMutation({
        variables: {
          input: {
            recurlySubscriptionId: subscriptionId,
            moduleId: moduleId,
          },
        },
      });
    }
  };

  const onCloseResume = () => {
    events.overview.closedResume({ petId: petId, currentSku: sku });
    closeResume();
  };

  function renderResumeConfirm() {
    return (
      <Stack>
        <Text fz={24} fw={600}>
          {t('resume_membership')}
        </Text>
        {isPaused ? (
          <Text fz={16} fw={600}>
            {t('your_access_to_the_fi_app_will_automatically_resume_and_youll_be_billed_today')}
          </Text>
        ) : (
          <Text fz={16} fw={600}>
            {t('your_access_to_the_fi_app_will_continue_and_youll_be_billed_on_date', { date: renewalDateString })}
          </Text>
        )}
        <Button onClick={onConfirmResume} loading={resumeMutationState.loading}>
          {t('confirm')}
        </Button>
        <Button onClick={onCloseResume} tertiary>
          {t('nevermind')}
        </Button>
      </Stack>
    );
  }

  const onConfirmReactivate = () => {
    events.overview.confirmedReactivate({
      petId: petId,
      currentSku: sku,
    });
    renewMutation({
      variables: {
        input: {
          moduleId: moduleId,
        },
      },
    });
  };

  const onCloseReactivate = () => {
    events.overview.closedReactivate({
      petId: petId,
      currentSku: sku,
    });
    closeReactivate();
  };

  const handleConvertTrialClicked = () => {
    if (hasSubscription && subscriptionId) {
      openConvertTrial();
    }
  };

  const onConfirmConvertTrial = () => {
    if (!subscriptionId) {
      notifications.show({
        title: t('something_went_wrong'),
        message: t('please_try_again_later_or_contact_support'),
        color: 'red',
        autoClose: false,
      });
    } else {
      convertTrialMutation({
        variables: {
          input: {
            subscriptionId,
          },
        },
      });
    }
  };

  const onCloseConvertTrial = () => {
    closeConvertTrial();
  };

  function renderConvertTrialConfirm() {
    return (
      <ConvertTrialConfirmContent
        priceInCents={priceInCents!}
        renewalMonths={renewalMonths!}
        onConfirm={onConfirmConvertTrial}
        onClose={onCloseConvertTrial}
        loading={convertTrialMutationState.loading}
      />
    );
  }

  function renderReactivateConfirm() {
    return (
      <Stack>
        <Text fz={24} fw={600}>
          {t('reactivate_membership')}
        </Text>
        <Text fz={16} fw={600}>
          {t('youll_be_billed_on_date_and_wont_lose_access_to_pet_name_steps_location_or_health_insights', {
            date: renewalDateString,
            petName,
          })}
        </Text>
        <Button onClick={onConfirmReactivate} loading={renewMutationState.loading}>
          {t('confirm')}
        </Button>
        <Button tertiary onClick={onCloseReactivate}>
          {t('nevermind')}
        </Button>
      </Stack>
    );
  }

  return (
    <>
      {largeScreen ? (
        <Modal
          opened={openedResume}
          onClose={onCloseResume}
          closeButtonProps={{ iconSize: 24, size: 30, color: 'dark', radius: 'xl' }}
          centered
          radius={16}
        >
          {renderResumeConfirm()}
        </Modal>
      ) : (
        <Drawer
          onClose={onCloseResume}
          opened={openedResume}
          position="bottom"
          size={345}
          closeButtonProps={{ iconSize: 24, size: 30, color: 'dark' }}
          styles={() => ({
            inner: {
              '.mantine-Drawer-content': {
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
              },
            },
            close: {
              height: 32,
              width: 32,
            },
          })}
        >
          {renderResumeConfirm()}
        </Drawer>
      )}
      {largeScreen ? (
        <Modal
          opened={openedReactivate}
          onClose={onCloseReactivate}
          closeButtonProps={{ iconSize: 24, size: 30, color: 'dark', radius: 'xl' }}
          centered={largeScreen}
          radius={16}
        >
          {renderReactivateConfirm()}
        </Modal>
      ) : (
        <Drawer
          opened={openedReactivate}
          onClose={onCloseReactivate}
          position="bottom"
          size={370}
          closeButtonProps={{ iconSize: 24, size: 30, color: 'dark' }}
          styles={() => ({
            inner: {
              '.mantine-Drawer-content': {
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
              },
            },
            close: {
              height: 32,
              width: 32,
            },
          })}
        >
          {renderReactivateConfirm()}
        </Drawer>
      )}
      <AddSubscriptionModal
        opened={openedAddSubscription}
        onClose={onCloseAddSubscription}
        moduleId={moduleId}
        refetchAllSubscriptions={refetchAllSubscriptions}
      />
      {largeScreen ? (
        <Modal
          opened={openedConvertTrial}
          onClose={onCloseConvertTrial}
          closeButtonProps={{ iconSize: 24, size: 30, color: 'dark', radius: 'xl' }}
          centered
          radius={16}
        >
          {renderConvertTrialConfirm()}
        </Modal>
      ) : (
        <Drawer
          opened={openedConvertTrial}
          onClose={onCloseConvertTrial}
          position="bottom"
          size={370}
          closeButtonProps={{ iconSize: 24, size: 30, color: 'dark' }}
          styles={() => ({
            inner: {
              '.mantine-Drawer-content': {
                borderTopLeftRadius: 16,
                borderTopRightRadius: 16,
              },
            },
            close: {
              height: 32,
              width: 32,
            },
          })}
        >
          {renderConvertTrialConfirm()}
        </Drawer>
      )}
      <Card radius={24} withBorder py={0} px={0}>
        <StatusSection
          hasDevice={hasDevice}
          hasSubscription={hasSubscription}
          canManageSubscription={canManageSubscription}
          billingEmail={billingEmail ?? ''}
          petName={petName}
          isCurrentlyPaused={isCurrentlyPaused}
          willPause={willPause}
          isDiscounted={isDiscounted}
          isCanceled={isCanceled}
          isCanceledFreeTrial={isCanceledFreeTrial}
          renewalPriceString={renewalPriceString}
          isExpired={isExpired}
          expirationDateString={expirationDateString}
          renewalDateString={renewalDateString}
          pendingCxResponse={pendingCxResponse ?? false}
          pendingChange={pendingChange}
          lastPaymentFailed={lastPaymentFailed}
        />
        <Stack px={24} py={20}>
          <Group spacing={8} position="apart" align="stretch">
            <Stack spacing={8}>
              <Group spacing={8}>
                <Avatar src={avatarUrl ?? avatarPlaceholder} radius="xl" size={24}>
                  <Image image={{ type: 'image', url: avatarPlaceholder }} />
                </Avatar>
                <Text color="type-light-primary" fw={600}>
                  {petName}
                </Text>
              </Group>
              <Text color="type-light-primary" fw={700} fz={32}>
                {isActiveFreeTrial || isCanceledFreeTrial ? t('free_trial_label') : description}
              </Text>
              {hasSubscription && subscriptionDateString ? (
                <Text color="type-light-secondary" fw={500} fz={12}>
                  {subscriptionDateString}
                </Text>
              ) : (
                <></>
              )}
            </Stack>
            {/* TODO: fix height */}
            {/* TODO: move away from sx prop when possible to make migration to Mantine 7 easier */}
            <Stack pr={0} spacing={16}>
              <Text size={12} color="type-light-secondary" fw={600} align="center" sx={{ minHeight: 20 }}>
                {(modelName ?? '').toUpperCase()}
              </Text>
              <ActionIcon
                component={Link}
                to={{ pathname: AppPaths.WebSubscription.Modify(moduleId), state: location.state }}
                onClick={() => events.overview.edit({ petId: petId, currentSku: sku })}
                w={75}
                radius={0}
                disabled={!canManageSubscription || pendingCxResponse}
                variant="transparent"
                sx={{
                  '&[data-disabled]': { opacity: 0.4, backgroundColor: 'transparent', borderColor: 'transparent' },
                }}
              >
                <Image image={{ type: 'image', url: EditIcon }} width={24} height={24} hidden={!showEditIcon} />
              </ActionIcon>
            </Stack>
          </Group>
          {(isCurrentlyPaused || willPause) && (
            <MantineButton color="dark" radius="lg" onClick={handleResumeClicked}>
              {t('resume_membership')}
            </MantineButton>
          )}
          {isActiveFreeTrial && (
            <MantineButton color="dark" radius="lg" onClick={handleConvertTrialClicked}>
              {t('upgrade_to_fi_membership')}
            </MantineButton>
          )}
          {/* TODO: figure out how to best handle the <24hrs where the subscription is expired,
           but we haven't cleared it from the device so the user can't buy a new one
           https://app.asana.com/1/553833434016848/project/1207222017559586/task/1211443974173313?focus=true */}
          {hasSubscription && isCanceled && !isExpired && (
            <MantineButton
              color="dark"
              radius="lg"
              onClick={isCanceledFreeTrial ? handleConvertTrialClicked : handleReactivateClicked}
            >
              {isCanceledFreeTrial ? t('upgrade_to_fi_membership') : t('reactivate')}
            </MantineButton>
          )}
          {hasDevice && !hasSubscription && (
            <MantineButton color="dark" radius="lg" onClick={handleAddSubscription}>
              {t('add_membership')}
            </MantineButton>
          )}
        </Stack>
      </Card>
    </>
  );
}
